import { useState } from "react";
import { copy, formatReviewWhen } from "../copy";
import { sourceShort, topicSubtitle, topicTitle } from "../domain/display";
import { reviewWhen } from "../domain/reviewQueue";
import { shanghaiTimeLabel } from "../domain/today";
import { oneLineReason, sourceLinkList } from "../domain/tasks";
import type { Topic } from "../types";
import { GenerateMenu } from "./GenerateMenu";
import { StagePill } from "./StagePill";

export function TopicTable({
  topics,
  selected,
  cursorId,
  onToggle,
  onToggleAll,
  onOpen,
  onDecide,
}: {
  topics: Topic[];
  selected?: ReadonlySet<string>;
  cursorId?: string | null;
  onToggle?: (id: string) => void;
  onToggleAll?: (checked: boolean) => void;
  onOpen: (topic: Topic) => void;
  onDecide?: (topic: Topic, status: "已确认" | "不做") => void;
  showStatus?: boolean;
}) {
  const selectable = Boolean(onToggle);
  const allChecked = selectable && topics.length > 0 && topics.every((topic) => selected?.has(topic.id));
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <div className={selectable ? "topic-list selectable" : "topic-list"}>
      <div className="topic-row topic-head">
        {selectable ? (
          <span>
            <input
              type="checkbox"
              checked={allChecked}
              aria-label={copy.decide.all}
              onChange={(event) => onToggleAll?.(event.target.checked)}
            />
          </span>
        ) : null}
        <span>{copy.overview.columns.title}</span>
        <span className="col-source">{copy.overview.columns.source}</span>
        <span>{copy.overview.columns.status}</span>
        <span className="col-plan">{copy.overview.columns.plan}</span>
        <span>{copy.overview.columns.actions}</span>
      </div>
      {topics.map((topic) => {
        const when = reviewWhen(topic);
        const time = shanghaiTimeLabel(topic.plannedPublishAt);
        const plan = topic.plannedPublishDate ? formatReviewWhen(when) : time && time !== "—" ? time : copy.schedule.unscheduled;
        const source = sourceShort(topic);
        return (
          <div
            key={topic.id}
            data-topic-id={topic.id}
            className={cursorId === topic.id ? "topic-row is-cursor" : "topic-row"}
            onClick={() => onOpen(topic)}
          >
            {selectable ? (
              <span onClick={(event) => event.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected?.has(topic.id) ?? false}
                  aria-label={topicTitle(topic)}
                  onChange={() => onToggle?.(topic.id)}
                />
              </span>
            ) : null}
            <span className="topic-title">
              <span className="row-title">
                {typeof topic.recommendRank === "number" ? <em className="rank-mark">{topic.recommendRank}</em> : null}
                {topicTitle(topic)}
              </span>
              {oneLineReason(topic) || topicSubtitle(topic) ? <span className="row-sub">{oneLineReason(topic) || topicSubtitle(topic)}</span> : null}
              {topic.syncError ? <span className="inline-error">{topic.syncError}</span> : null}
            </span>
            <span className="clip col-source" title={source || undefined}>{source || copy.dash}</span>
            <span>
              <StagePill status={topic.status} />
            </span>
            <span className="clip plan-cell col-plan" title={plan}>{plan}</span>
            <span className="topic-ops" onClick={(event) => event.stopPropagation()}>
              {hasDetail(topic) ? (
                <button type="button" className="button ghost" aria-expanded={openId === topic.id} onClick={() => setOpenId((current) => current === topic.id ? null : topic.id)}>
                  {openId === topic.id ? copy.recommend.collapse : copy.recommend.expand}
                </button>
              ) : null}
              <GenerateMenu topic={topic} />
              {onDecide && topic.status === "待确认" ? (
                <>
                  <button type="button" className="button line" onClick={() => onDecide(topic, "已确认")}>{copy.doIt}</button>
                  <button type="button" className="button ghost" onClick={() => onDecide(topic, "不做")}>{copy.skip}</button>
                </>
              ) : (
                <button type="button" className="button ghost" onClick={() => onOpen(topic)}>{copy.library.open}</button>
              )}
            </span>
            {openId === topic.id ? <TopicDetail topic={topic} /> : null}
          </div>
        );
      })}
    </div>
  );
}

function hasDetail(topic: Topic): boolean {
  return Boolean(topic.reason || topic.viralReason || sourceLinkList(topic).length || topic.sourcePlatform);
}

function TopicDetail({ topic }: { topic: Topic }) {
  const links = sourceLinkList(topic).flatMap((value) => {
    const href = httpUrl(value);
    return href ? [{ href, label: value }] : [];
  });
  return (
    <div className="topic-more" onClick={(event) => event.stopPropagation()}>
      {topic.reason ? (
        <p>
          <b>{copy.recommend.reason}</b>
          {topic.reason}
        </p>
      ) : null}
      {topic.viralReason ? (
        <p>
          <b>{copy.recommend.viral}</b>
          {topic.viralReason}
        </p>
      ) : null}
      {topic.sourcePlatform ? (
        <p>
          <b>{copy.recommend.platform}</b>
          {topic.sourcePlatform}
        </p>
      ) : null}
      {links.length ? (
        <p>
          <b>{copy.recommend.links}</b>
          {links.map((link) => (
            <a key={link.href} href={link.href} target="_blank" rel="noreferrer">{link.label}</a>
          ))}
        </p>
      ) : null}
    </div>
  );
}

function httpUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}
